import React, { useCallback } from 'react';
import classNames from 'classnames';

import { Icon, Button, Typography } from '@canvas-logic/clp-shared-ui';

import { useMediaQuery } from '@/ui-kit/hooks/use-media-query';

type ItemValue = string | number;

interface IProps<T extends ItemValue> {
  orientation?: 'horizontal' | 'vertical';
  label?: string;
  description?: string;
  hasTooltip?: boolean;
  cost?: string;
  icon?: React.JSX.Element;
  imageSrc?: string;
  imageType?: 'image' | 'icon';
  value: T;
  hasCheck?: boolean;
  /**
   * When selected & disabled, has specific appearance
   */
  hasAlwaysOn?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onTooltipClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSelect?: (value: T) => void;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Card = <T extends ItemValue>({
  className,
  icon,
  imageSrc,
  imageType,
  label,
  description,
  hasTooltip,
  cost,
  value,
  hasCheck,
  hasAlwaysOn,
  onClick,
  onTooltipClick,
  onSelect,
  selected,
  disabled,
  orientation = 'vertical',
}: IProps<T>) => {
  const supportsHover = useMediaQuery('(hover: hover)');

  const handleTooltipClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      onTooltipClick?.(event);
      event.currentTarget.blur();
    },
    [onTooltipClick],
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event);
    onSelect?.(value);
  };

  const hasImage = icon || imageSrc;
  const hasText = label || description || cost;
  const isAlwaysOn = hasAlwaysOn && selected && disabled;
  const hasHoverTooltipButton = supportsHover && hasTooltip && orientation === 'vertical';

  return (
    <div
      data-card
      className={classNames(
        'cp-card',
        {
          'cp-card--selected': selected,
          'cp-card--alwaysOn': isAlwaysOn,
          'cp-card--disabled': disabled,
        },
        className,
      )}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={classNames('cp-card__content', {
          'cp-card__content--horizontal': orientation === 'horizontal',
          'cp-card__content--disabled': disabled,
        })}
        onClick={handleClick}
        aria-disabled={disabled}
      >
        {hasCheck ? (
          <div
            className={classNames('cp-card__check', {
              'cp-card__check--selected': selected,
              'cp-card__check--disabled': disabled,
            })}
          />
        ) : null}
        {isAlwaysOn ? (
          <div className={classNames('cp-card__lock')}>
            <Icon type="lock" />
          </div>
        ) : null}
        {hasImage ? (
          <div className={classNames('cp-card__image-wrapper')}>
            {hasHoverTooltipButton && (
              <div className={'cp-card__tooltip-wrapper'}>
                <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
              </div>
            )}
            {icon}
            {imageSrc && (
              <img
                className={classNames('cp-card__image', {
                  'cp-card__image--icon': imageType === 'icon',
                  'cp-card__image--horizontal': orientation === 'horizontal',
                })}
                src={imageSrc}
                alt={label ?? description}
              />
            )}
          </div>
        ) : null}
        {hasText ? (
          <div className={classNames('cp-card__text')}>
            {!hasImage && hasHoverTooltipButton && (
              <div className={'cp-card__tooltip-wrapper'}>
                <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
              </div>
            )}
            {label ? <div className={'cp-card__title'}>{label}</div> : null}
            {description ? <div className={'cp-card__description'}>{description}</div> : null}
            {cost ? <div className={'cp-card__cost body-lg'}>{cost}</div> : null}
          </div>
        ) : null}
        {hasTooltip && orientation === 'horizontal' && (
          <div className={'cp-card__tooltip-wrapper--horizontal'}>
            <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
          </div>
        )}
      </div>
    </div>
  );
};
